import { useEffect, useMemo, useState } from 'react';
import * as Y from 'yjs';
import type { Shape } from '@frame/shared';

// Subscribes to the shapes Y.Map and returns a plain Shape[] snapshot.
// observeDeep catches both Map-level changes (add/delete) and per-shape Map
// updates (x/y on drag). For O(100) shapes this re-snapshots fast enough;
// optimize with key-stable references if profiling shows it later.
export function useShapes(doc: Y.Doc): Shape[] {
  const yShapes = useMemo(() => doc.getMap<Y.Map<unknown>>('shapes'), [doc]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const handler = () => setVersion((v) => v + 1);
    yShapes.observeDeep(handler);
    return () => yShapes.unobserveDeep(handler);
  }, [yShapes]);

  return useMemo(() => {
    const result: Shape[] = [];
    yShapes.forEach((ymap) => {
      const obj: Record<string, unknown> = {};
      ymap.forEach((v, k) => {
        obj[k] = v;
      });
      result.push(obj as unknown as Shape);
    });
    // Sort by z (explicit render order). Shapes from before z landed default
    // to 0 so they keep their original Y.Map iteration order — stable sort
    // means ties resolve to insertion order.
    result.sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
    return result;
    // version is the cache-buster; yShapes content is the actual source
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yShapes, version]);
}
